// Client-side PDF export (CONTRACTS §10). We snapshot the CURRENT rendered DOM
// of the doc iframe (post-script state), strip scripts AND inline event handlers
// AND javascript: URLs, render the snapshot into a SANDBOXED print iframe (no
// allow-scripts, allow-modals so print() works), and stamp the mandatory © Loop
// Data footer + CONFIDENTIAL marking onto EVERY page.
//
// Two hardening properties matter:
//  - The sandbox (no allow-scripts) is the real guarantee that an untrusted
//    bundle's JS cannot run same-origin during export. The attribute scrub is
//    defense-in-depth.
//  - The stamp styles are set INLINE with !important. An important inline
//    declaration outranks any selector-based author !important rule (the style
//    attribute has the highest specificity), so bundle CSS in the same document
//    cannot hide the marking — and we lock every hiding vector (display,
//    visibility, opacity, transform, filter, clip), not just display.

import { serializeCleanHtml } from "../annotation/highlighter";
import { CONFIDENTIAL_FOOTER, CONFIDENTIAL_LABEL } from "./labels";

const PAGE_CSS = `@media print { @page { margin: 18mm 14mm 22mm; } }`;

// Properties that, if left to the bundle, could hide the stamp. We force all of
// them inline-important. An important declaration in an element's style attribute
// outranks any author *stylesheet* !important rule (the style attribute has the
// highest specificity), and a sandboxed bundle has no JS to add inline styles to
// our nodes — so for every property locked here, no bundle CSS can win. We cover
// both box-hiding (display/visibility/opacity/transform/filter/clip) AND
// text-hiding (text-fill-color/indent/letter-spacing/font/shadow) vectors.
const LOCK: Record<string, string> = {
  display: "block",
  visibility: "visible",
  opacity: "1",
  transform: "none",
  filter: "none",
  "clip-path": "none",
  clip: "auto",
  "content-visibility": "visible",
  position: "fixed",
  "z-index": "2147483647",
  margin: "0",
  "max-height": "none",
  "max-width": "none",
  height: "auto",
  width: "auto",
  overflow: "visible",
  "pointer-events": "none",
  // Text-rendering vectors a bundle could otherwise use to blank the text.
  "-webkit-text-fill-color": "currentColor",
  "-webkit-text-stroke": "0",
  "text-indent": "0",
  "text-shadow": "none",
  "letter-spacing": "normal",
  "word-spacing": "normal",
  "white-space": "normal",
  "text-transform": "none",
  zoom: "1",
};

function stampElement(
  doc: Document,
  className: string,
  text: string,
  extra: Record<string, string>
): HTMLElement {
  const el = doc.createElement("div");
  el.className = className;
  el.textContent = text;
  const style = { ...LOCK, ...extra };
  for (const [k, v] of Object.entries(style)) el.style.setProperty(k, v, "important");
  return el;
}

/** Remove inline event-handler attributes and javascript: URLs from a tree. */
function scrubActiveContent(root: ParentNode): void {
  for (const el of Array.from(root.querySelectorAll("*"))) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
        continue;
      }
      if (
        (name === "href" || name === "src" || name === "xlink:href") &&
        /^\s*javascript:/i.test(attr.value)
      ) {
        el.removeAttribute(attr.name);
      }
    }
  }
}

/** Build a clean, inert (no JS), stamped HTML snapshot for printing. */
function buildPrintHtml(sourceDoc: Document, baseHref?: string): string {
  const raw = serializeCleanHtml(sourceDoc);
  const parsed = new DOMParser().parseFromString(raw, "text/html");

  // Inert the snapshot: drop scripts + inline handlers + javascript: URLs. The
  // print frame is also sandboxed without allow-scripts (printDoc), so this is
  // belt-and-suspenders — but it keeps the written HTML clean regardless.
  parsed.querySelectorAll("script").forEach((s) => s.remove());
  scrubActiveContent(parsed);

  const head = parsed.head ?? parsed.documentElement;
  if (baseHref) {
    const base = parsed.createElement("base");
    base.href = baseHref;
    head.insertBefore(base, head.firstChild);
  }
  const pageStyle = parsed.createElement("style");
  pageStyle.textContent = PAGE_CSS;
  head.appendChild(pageStyle);

  const body = parsed.body ?? parsed.documentElement;
  // Confidential marking (top-right) + copyright footer (bottom), styled inline
  // with !important so bundle CSS cannot suppress them. position:fixed repeats
  // them on every printed page.
  const label = stampElement(parsed, "soapbox-print-confidential", CONFIDENTIAL_LABEL, {
    top: "0",
    right: "0",
    left: "auto",
    bottom: "auto",
    color: "#b84239",
    font: '700 9px/1 -apple-system, "Helvetica Neue", Arial, sans-serif',
    "letter-spacing": "0.14em",
    padding: "6px 12px",
  });
  const footer = stampElement(parsed, "soapbox-print-footer", CONFIDENTIAL_FOOTER, {
    left: "0",
    right: "0",
    bottom: "0",
    top: "auto",
    color: "#4a4a55",
    font: '9px/1.35 -apple-system, "Helvetica Neue", Arial, sans-serif',
    "text-align": "center",
    padding: "6px 14px",
    background: "transparent",
  });
  body.appendChild(label);
  body.appendChild(footer);

  return "<!DOCTYPE html>\n" + parsed.documentElement.outerHTML;
}

/**
 * Open the browser print dialog for a stamped snapshot of `sourceDoc`. The print
 * iframe is sandboxed (allow-same-origin so the parent can write it + allow-modals
 * so print() works) but WITHOUT allow-scripts, so no snapshot JS can run.
 * `baseHref` should be the content directory (e.g. `/content/{docId}/{versionId}/`).
 */
export async function printDoc(
  sourceDoc: Document,
  opts: { baseHref?: string } = {}
): Promise<void> {
  const html = buildPrintHtml(sourceDoc, opts.baseHref);

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  // No allow-scripts: snapshot JS (incl. inline handlers) cannot execute here.
  frame.setAttribute("sandbox", "allow-same-origin allow-modals");
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(frame);

  const fdoc = frame.contentDocument;
  if (!fdoc) {
    frame.remove();
    throw new Error("Could not open a print view.");
  }
  fdoc.open();
  fdoc.write(html);
  fdoc.close();

  // Give layout, images and fonts a moment to settle before printing.
  await new Promise((r) => setTimeout(r, 250));
  try {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
  } finally {
    setTimeout(() => frame.remove(), 1500);
  }
}
